import { useSessionTokenGetter } from '@develio/auth/client';
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  type OrgMember,
  STATUS_COLOR,
  STATUS_LABEL,
  type Submission,
  type SubmissionStatus,
  formatAge,
} from './submissionUtils';

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

type TabKey = 'all' | SubmissionStatus;

const TABS: { key: TabKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'new', label: 'New' },
  { key: 'in_review', label: 'In Review' },
  { key: 'converted', label: 'Converted' },
  { key: 'rejected', label: 'Rejected' },
];

// ── StatusCell ────────────────────────────────────────────────────────────────
const QUICK_STATUSES: SubmissionStatus[] = ['new', 'in_review', 'rejected'];

function StatusCell({
  sub,
  onUpdate,
  disabled,
}: {
  sub: Submission;
  onUpdate: (subId: string, status: SubmissionStatus) => void;
  disabled: boolean;
}) {
  const [open, setOpen] = useState(false);

  if (sub.status === 'converted') {
    return (
      <span
        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLOR.converted}`}
      >
        {STATUS_LABEL.converted}
      </span>
    );
  }

  return (
    <div className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium disabled:opacity-50 ${STATUS_COLOR[sub.status]}`}
      >
        {STATUS_LABEL[sub.status]}
      </button>
      {open && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setOpen(false)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setOpen(false);
            }}
            aria-hidden="true"
          />
          <div className="absolute left-0 z-20 mt-1 w-36 overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg">
            {QUICK_STATUSES.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => {
                  setOpen(false);
                  onUpdate(sub.id, s);
                }}
                className={`flex w-full items-center px-3 py-2 text-xs hover:bg-slate-50 ${
                  sub.status === s ? 'font-semibold' : ''
                }`}
              >
                <span
                  className={`mr-2 inline-block h-2 w-2 rounded-full ${
                    s === 'new'
                      ? 'bg-blue-400'
                      : s === 'in_review'
                        ? 'bg-amber-400'
                        : 'bg-slate-300'
                  }`}
                />
                {STATUS_LABEL[s]}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Main route ────────────────────────────────────────────────────────────────
export function FormSubmissionsRoute() {
  const { id: formId } = useParams<{ id: string }>();
  const getToken = useSessionTokenGetter();

  const [formName, setFormName] = useState('');
  const [allSubmissions, setAllSubmissions] = useState<Submission[]>([]);
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('all');
  const [updating, setUpdating] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!formId) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const [formRes, subRes, membersRes] = await Promise.all([
          fetch(`${API}/api/v1/intake-forms/${formId}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${API}/api/v1/intake-forms/${formId}/submissions`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${API}/api/v1/members`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        if (!formRes.ok || !subRes.ok) throw new Error('Failed to load data');
        const formJson = await formRes.json();
        const subJson = await subRes.json();
        if (!cancelled) {
          setFormName(formJson.form.name as string);
          setAllSubmissions(subJson.submissions as Submission[]);
          if (membersRes.ok) {
            const membersJson = await membersRes.json();
            setMembers((membersJson.members ?? []) as OrgMember[]);
          }
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [formId, getToken]);

  const handleStatusChange = useCallback(
    async (subId: string, status: SubmissionStatus) => {
      setUpdating((prev) => ({ ...prev, [subId]: true }));
      try {
        const token = await getToken();
        const res = await fetch(`${API}/api/v1/intake-forms/${formId}/submissions/${subId}`, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ status }),
        });
        if (!res.ok) return;
        setAllSubmissions((prev) => prev.map((s) => (s.id === subId ? { ...s, status } : s)));
      } finally {
        setUpdating((prev) => ({ ...prev, [subId]: false }));
      }
    },
    [formId, getToken],
  );

  const handleAssigneeChange = useCallback(
    async (subId: string, assignedTo: string | null) => {
      setUpdating((prev) => ({ ...prev, [subId]: true }));
      try {
        const token = await getToken();
        const res = await fetch(`${API}/api/v1/intake-forms/${formId}/submissions/${subId}`, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ assignedTo }),
        });
        if (!res.ok) return;
        const member = members.find((m) => m.userId === assignedTo);
        setAllSubmissions((prev) =>
          prev.map((s) =>
            s.id === subId
              ? {
                  ...s,
                  assignedTo,
                  assigneeName: member?.name ?? null,
                  assigneeEmail: member?.email ?? null,
                }
              : s,
          ),
        );
      } finally {
        setUpdating((prev) => ({ ...prev, [subId]: false }));
      }
    },
    [formId, getToken, members],
  );

  // ── Counts per status ───────────────────────────────────────────────────────
  const counts: Record<TabKey, number> = {
    all: allSubmissions.length,
    new: allSubmissions.filter((s) => s.status === 'new').length,
    in_review: allSubmissions.filter((s) => s.status === 'in_review').length,
    converted: allSubmissions.filter((s) => s.status === 'converted').length,
    rejected: allSubmissions.filter((s) => s.status === 'rejected').length,
  };

  const displayed =
    activeTab === 'all' ? allSubmissions : allSubmissions.filter((s) => s.status === activeTab);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Link to="/forms" className="hover:text-slate-900 hover:underline">
              ← Forms
            </Link>
            {formName && (
              <>
                <span>/</span>
                <Link to={`/forms/${formId}/edit`} className="hover:text-slate-900 hover:underline">
                  {formName}
                </Link>
              </>
            )}
          </div>
          <h2 className="mt-1 text-2xl font-semibold tracking-tight">Submissions</h2>
        </div>
        {formId && (
          <Link
            to={`/forms/${formId}/edit`}
            className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            ← Back to editor
          </Link>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div className="rounded-lg border border-slate-200 bg-white p-8 text-center">
          <p className="text-sm text-slate-500">Loading…</p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {!loading && !error && (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          {/* Status tabs */}
          <div className="flex border-b border-slate-200">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab.key
                    ? 'border-b-2 border-slate-900 text-slate-900'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {tab.label}
                <span
                  className={`rounded-full px-1.5 py-0.5 text-xs ${
                    activeTab === tab.key
                      ? 'bg-slate-900 text-white'
                      : 'bg-slate-100 text-slate-500'
                  }`}
                >
                  {counts[tab.key]}
                </span>
              </button>
            ))}
          </div>

          {/* Empty state */}
          {displayed.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-sm text-slate-500">
                {activeTab === 'all'
                  ? 'No submissions yet. Share or embed your form to start collecting responses.'
                  : `No ${STATUS_LABEL[activeTab as SubmissionStatus] ?? activeTab} submissions.`}
              </p>
            </div>
          ) : (
            <>
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-slate-500">#</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-500">Submitter</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-500">Email</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-500">Age</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-500">Status</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-500">Assigned To</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {displayed.map((sub, idx) => {
                    const age = formatAge(sub.createdAt);
                    const isActionable = sub.status === 'new' || sub.status === 'in_review';
                    return (
                      <tr key={sub.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 tabular-nums text-slate-400">{idx + 1}</td>
                        <td className="px-4 py-3 font-medium text-slate-900">
                          {sub.submitterName ?? <span className="italic text-slate-400">—</span>}
                        </td>
                        <td className="px-4 py-3 text-slate-500">
                          {sub.submitterEmail ? (
                            <a href={`mailto:${sub.submitterEmail}`} className="hover:underline">
                              {sub.submitterEmail}
                            </a>
                          ) : (
                            <span className="italic text-slate-400">—</span>
                          )}
                        </td>
                        <td
                          className={`px-4 py-3 ${
                            age.urgent && isActionable
                              ? 'font-medium text-amber-600'
                              : 'text-slate-500'
                          }`}
                        >
                          {age.label}
                        </td>
                        <td className="px-4 py-3">
                          <StatusCell
                            sub={sub}
                            onUpdate={handleStatusChange}
                            disabled={!!updating[sub.id]}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <select
                            value={sub.assignedTo ?? ''}
                            onChange={(e) => handleAssigneeChange(sub.id, e.target.value || null)}
                            disabled={!!updating[sub.id]}
                            className="rounded border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 disabled:opacity-50"
                          >
                            <option value="">Unassigned</option>
                            {members.map((m) => (
                              <option key={m.userId} value={m.userId}>
                                {m.name ?? m.email}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Link
                            to={`/forms/${formId}/submissions/${sub.id}`}
                            className="text-xs font-medium text-slate-500 hover:text-slate-900 hover:underline"
                          >
                            View →
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="border-t border-slate-100 px-4 py-2 text-xs text-slate-400">
                {displayed.length} submission{displayed.length !== 1 ? 's' : ''}
                {activeTab !== 'all' && ` · ${STATUS_LABEL[activeTab as SubmissionStatus]}`}
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
